export const SECTION_HEADER_SX = {
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;
export const BOLD_SX = { fontWeight: 700 } as const;
export const SECONDARY_TEXT_SX = { color: 'text.secondary' } as const;
export const BORDERED_BOX_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '4px',
} as const;
export const BORDER_RIGHT = {
  borderRight: '1px solid',
  borderColor: 'divider',
} as const;
export const BORDER_LEFT = {
  borderLeft: '1px solid',
  borderColor: 'divider',
} as const;
export const TRUNCATE_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
// MUI class selectors target the internal DOM of ovComponents TextField/SelectField for compact sizing.
// ovComponents do not expose size/padding props for this level of control.
export const BLOTTER_INPUT_SX = {
  '& .MuiInputBase-root': { height: 26 },
  '& .MuiSelect-select': { py: '2px', px: '8px' },
} as const;
export const BLOTTER_LABEL_SMALL_SX = {
  fontSize: '0.55rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
} as const;
export const BLOTTER_BADGE_SX = {
  px: 0.5,
  py: '1px',
  borderRadius: '3px',
} as const;
export const CARD_HEADER_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
} as const;
export const SORTABLE_HEADER_SX = {
  fontWeight: 700,
  textTransform: 'uppercase',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { color: 'text.primary' },
} as const;

export const ALIGNED_TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& thead': { display: 'table-header-group' },
  '& tbody': { display: 'table-row-group' },
} as const;
export const VALUE_ROW_SX = {
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
} as const;

export const LABEL_SX = { fontSize: '0.65rem', fontWeight: 600 } as const;
export const LABEL_UPPER_SX = {
  fontSize: '0.65rem',
  fontWeight: 600,
  textTransform: 'uppercase',
} as const;
export const VALUE_SX = {
  fontSize: '0.7rem',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
} as const;
export const MICRO_SX = { fontSize: '0.55rem' } as const;
export const MICRO_UPPER_SX = {
  fontSize: '0.55rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
} as const;
export const SMALL_SX = { fontSize: '0.6rem' } as const;
export const SMALL_BOLD_SX = { fontSize: '0.6rem', fontWeight: 600 } as const;
export const TABLE_HEAD_SX = {
  fontSize: '0.6rem',
  fontWeight: 600,
  textTransform: 'uppercase',
} as const;
export const TABULAR_SX = { fontVariantNumeric: 'tabular-nums' } as const;
export const COMPACT_TEXT_FIELD_SX = {
  '& .MuiInputBase-root': { height: 28 },
  '& .MuiOutlinedInput-input': { py: '4px' },
} as const;
export const COMPACT_SELECT_FIELD_SX = {
  '& .MuiInputBase-root': { height: 28 },
  '& .MuiSelect-select': { py: '4px' },
} as const;

// Restyles legacy fill tables to match the prototype design system.
// Remove once those tables are replaced with ovComponent equivalents.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FILLS_FULL_WIDTH_SX = (accent: string, sys: any) => ({
  border: '1px solid',
  borderColor: sys.color.outlineVariant,
  borderRadius: '6px',
  overflow: 'hidden',
  '& > [data-testid] > .MuiGrid-root:first-of-type': { display: 'none' },
  '& .MuiTypography-h6': { display: 'none' },
  '& .MuiIconButton-root': { m: '4px 8px !important', p: '4px' },
  '& .MuiTable-root': { tableLayout: 'auto' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    py: '5px',
    px: '10px',
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: sys.color.onSurfaceVariant,
    borderBottom: '2px solid',
    borderColor: accent,
    bgcolor: sys.color.neutralHover,
    whiteSpace: 'nowrap',
    '& .MuiTypography-root': {
      fontSize: 'inherit',
      fontWeight: 'inherit',
      letterSpacing: 'inherit',
      textTransform: 'inherit',
    },
    '&:empty': { width: 0, px: 0, minWidth: 0 },
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    '&:nth-of-type(even)': { bgcolor: sys.color.neutralHover },
    '&:hover': {
      bgcolor: `color-mix(in srgb, ${accent || '#888'} 6%, transparent)`,
    },
    transition: 'background 0.1s ease',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    py: '4px',
    px: '10px',
    fontSize: '0.78rem',
    borderBottom: '1px solid',
    borderColor: sys.color.outlineVariant,
    verticalAlign: 'middle',
    '& .MuiTypography-root': { fontSize: '0.78rem' },
    '&:first-of-type': {
      fontWeight: 700,
      letterSpacing: '0.02em',
      '& .MuiTypography-root': { fontWeight: 700 },
    },
  },
  '& .MuiTextField-root': {
    maxWidth: 160,
    '& .MuiInputBase-root': {
      height: 28,
      fontSize: '0.78rem',
      borderRadius: '4px',
      bgcolor: sys.color.surface,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: sys.color.outlineVariant,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accent },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: accent,
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputAdornment-root .MuiTypography-root': {
      fontSize: '0.7rem',
      color: sys.color.onSurfaceVariant,
      fontWeight: 600,
    },
  },
  '& .MuiChip-root': {
    height: 20,
    fontSize: '0.55rem',
    fontWeight: 600,
    ml: '6px !important',
  },
  '& .MuiGrid-root:last-of-type': {
    borderTop: '1px solid',
    borderColor: sys.color.outlineVariant,
    bgcolor: sys.color.neutralHover,
    '& .MuiButton-contained': {
      m: '8px 12px !important',
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: '6px',
      height: 32,
      px: 2.5,
      bgcolor: `${accent} !important`,
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' },
    },
  },
  '& .MuiCircularProgress-root': { m: '16px !important' },
});
